import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SearchFormComponent, FormFieldConfig } from '../../../../../shared/components/search-form/search-form.component';
import { StatusFilterComponent, StatusOption, ActionButton } from '../../../../../shared/components/status-filter/status-filter.component';
import { DataTableComponent, TableColumn, PaginationEvent } from '../../../../../shared/components/data-table/data-table.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { signal, computed } from '@angular/core';
import { RefundAuditItem } from '../refund/refund-audit.component';

export interface ReturnAuditItem {
  id: number;
  internalRefundNo: string;
  thirdOrderNo: string;
  thirdRefundNo: string;
  internalOrderNo: string;
  refundBillType: string;
  refundType: string;
  refundSource: string;
  orderStatus: string;
  auditStatus: string;
  refundQty: number;
  totalRefundAmount: number;
  refundReason: string;
  buyerNote?: string;
  serviceNote?: string;
  buyerNickname: string;
  store: string;
  channel: string;
  thirdRefundTime?: string;
  internalCreateTime: string;
  completeTime?: string;
}

@Component({
  selector: 'app-return-audit',
  standalone: true,
  imports: [CommonModule, FormsModule, SearchFormComponent, StatusFilterComponent, DataTableComponent, TranslateModule],
  templateUrl: './return-audit.component.html',
  styleUrl: './return-audit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReturnAuditComponent implements OnInit, OnDestroy {
  private translateService = inject(TranslateService);

  searchFormat: FormFieldConfig[] = [];
  statusOptions: StatusOption[] = [];
  primaryButton: ActionButton = { label: 'after.audit.return.actions.approve', severity: 'info' };
  secondaryButton: ActionButton = { label: 'after.audit.return.actions.reject', severity: 'info', outlined: true };
  tableColumns: TableColumn[] = [];

  loading = signal(false);
  pageSize = signal(20);
  first = signal(0);
  totalRecords = signal(0);
  selected = signal<ReturnAuditItem[]>([]);
  data = signal<ReturnAuditItem[]>([]);

  pagedData = computed(() => {
    const start = this.first();
    const end = start + this.pageSize();
    return this.data().slice(start, end);
  });

  ngOnInit(): void {
    this.initializeSearchFormat();
    this.initializeStatusOptions();
    this.initializeTableColumns();
    this.mockLoad();
  }

  private initializeSearchFormat(): void {
    this.searchFormat = [
      { key: 'orderNumber', label: 'after.audit.return.search.order_number', type: 'text', placeholder: 'after.audit.return.search.order_number_placeholder' },
      { key: 'refundSource', label: 'after.audit.return.search.refund_source', type: 'select', options: this.getRefundSourceOptions() },
      { key: 'refundBillType', label: 'after.audit.return.search.refund_bill_type', type: 'select', options: this.getRefundBillTypeOptions() },
      { key: 'status', label: 'after.audit.return.search.status', type: 'select', options: this.getStatusOptions() },
      { key: 'buyerNickname', label: 'after.audit.return.search.buyer_nickname', type: 'text' },
      { key: 'salesChannel', label: 'after.audit.return.search.sales_channel', type: 'select', options: this.getSalesChannelOptions() },
      { key: 'store', label: 'after.audit.return.search.sales_store', type: 'select', options: [], disabled: true },
      { key: 'refundTime', label: 'after.audit.return.search.refund_time', type: 'dateRange', range: { startKey: 'refundTimeStart', endKey: 'refundTimeEnd' } },
    ];
  }

  private initializeStatusOptions(): void {
    this.statusOptions = [
      { key: 'all', label: 'after.audit.return.status.all', active: true },
      { key: 'first', label: 'after.audit.return.status.first_review_pending' },
      { key: 'second', label: 'after.audit.return.status.second_review_pending' },
      { key: 'rejected', label: 'after.audit.return.status.rejected' },
    ];
  }

  private initializeTableColumns(): void {
    this.tableColumns = [
      { label: 'after.audit.return.table.internal_refund_no', prop: 'internalRefundNo', width: 160 },
      { label: 'after.audit.return.table.third_order_no', prop: 'thirdOrderNo', width: 160 },
      { label: 'after.audit.return.table.third_refund_no', prop: 'thirdRefundNo', width: 160 },
      { label: 'after.audit.return.table.internal_order_no', prop: 'internalOrderNo', width: 160 },
      { label: 'after.audit.return.table.refund_bill_type', prop: 'refundBillType', width: 140 },
      { label: 'after.audit.return.table.refund_type', prop: 'refundType', width: 140 },
      { label: 'after.audit.return.table.refund_source', prop: 'refundSource', width: 140 },
      { label: 'after.audit.return.table.order_status', prop: 'orderStatus', width: 140 },
      { label: 'after.audit.return.table.audit_status', prop: 'auditStatus', width: 140 },
      { label: 'after.audit.return.table.refund_quantity', prop: 'refundQty', width: 120 },
      { label: 'after.audit.return.table.total_refund_amount', prop: 'totalRefundAmount', width: 160 },
      { label: 'after.audit.return.table.refund_reason', prop: 'refundReason', width: 200 },
      { label: 'after.audit.return.table.buyer_note', prop: 'buyerNote', width: 160 },
      { label: 'after.audit.return.table.service_note', prop: 'serviceNote', width: 160 },
      { label: 'after.audit.return.table.buyer_nickname', prop: 'buyerNickname', width: 140 },
      { label: 'after.audit.return.table.store', prop: 'store', width: 160 },
      { label: 'after.audit.return.table.channel', prop: 'channel', width: 160 },
      { label: 'after.audit.return.table.third_refund_time', prop: 'thirdRefundTime', width: 180 },
      { label: 'after.audit.return.table.internal_created_at', prop: 'internalCreateTime', width: 180 },
      { label: 'after.audit.return.table.completed_at', prop: 'completeTime', width: 180 },
    ];
  }

  getRefundSourceOptions() {
    return [
      { label: this.translateService.instant('after.audit.return.options.common_transfer'), value: 'Common Transfer' },
      { label: this.translateService.instant('after.audit.return.options.manual'), value: 'Manual' }
    ];
  }

  getRefundBillTypeOptions() {
    return [
      { label: this.translateService.instant('after.audit.return.options.normal'), value: 'Normal' }
    ];
  }

  getStatusOptions() {
    return [
      { label: this.translateService.instant('after.audit.return.options.pending'), value: 'Pending' },
      { label: this.translateService.instant('after.audit.return.options.first_review_pending'), value: 'First Review Pending' },
      { label: this.translateService.instant('after.audit.return.options.second_review_pending'), value: 'Second Review Pending' },
      { label: this.translateService.instant('after.audit.return.options.rejected'), value: 'Rejected' }
    ];
  }

  getSalesChannelOptions() {
    return [
      { label: this.translateService.instant('after.audit.return.options.facebook'), value: 'Facebook' },
      { label: this.translateService.instant('after.audit.return.options.shopee'), value: 'Shopee' },
      { label: this.translateService.instant('after.audit.return.options.tmall'), value: 'Tmall' }
    ];
  }

  ngOnDestroy(): void {}

  onSearch(): void {
    this.first.set(0);
    this.mockLoad();
  }

  onReset(): void {
    this.first.set(0);
    this.mockLoad();
  }

  onPageChange(event: PaginationEvent): void {
    this.first.set(event.first ?? this.first());
    this.pageSize.set(event.rows ?? this.pageSize());
  }

  onSelectionChange(rows: ReturnAuditItem[]): void {
    this.selected.set(rows);
  }

  onApprove(): void { this.selected.set([]); }
  onReject(): void { /* open dialog */ }

  private mockLoad(): void {
    this.loading.set(true);
    const rows: RefundAuditItem[] = [
      {
        id: 1,
        internalRefundNo: 'CO00005123',
        thirdOrderNo: '11',
        thirdRefundNo: '111111',
        internalOrderNo: 'CO00005117',
        refundBillType: 'Normal',
        refundType: 'Pre-shipment Refund Only',
        refundSource: 'Common Transfer',
        orderStatus: 'Pending',
        auditStatus: 'Second Review Pending',
        refundQty: 1,
        totalRefundAmount: 5656,
        refundReason: 'RE1731376868202',
        buyerNote: '',
        serviceNote: '',
        buyerNickname: 'buyer-1',
        store: 'Colorful Goods Warehouse',
        channel: 'TikTok Store',
        thirdRefundTime: '',
        internalCreateTime: '2024-11-27 09:28:57',
        completeTime: '',
      },
    ];
    this.data.set(rows as unknown as ReturnAuditItem[]);
    this.totalRecords.set(rows.length);
    this.loading.set(false);
  }
} 